import React, { useCallback, useEffect, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { blobErrorMessage } from "../../../../services/v2/inventory.service";
import ProductPicker from "./productPicker";
import { Empty, Loading, Pager, apiError, saveBlob, ui, useDebounced } from "../kits/shared";
import { Modal } from "../warehouses/traslados";

/*
	Productos → Catálogo viejo: revisar los productos de antes de sep-2026 para poder apagar el catálogo viejo sin perder
	historial. Pasar al nuevo (mismo registro), equivalente a uno nuevo, dar de baja, o dejar por ahora.
*/

const KINDS = [["article", "Artículo"], ["kit", "Kit"], ["uniform", "Uniforme"], ["service", "Servicio o cobro"], ["digital", "Digital"]];

const CatalogoViejo: React.FC<{ campusId: number; currency: string; canEdit: boolean; idleLocked: boolean }> = ({ campusId, currency, canEdit, idleLocked }) => {
	const http = HttpManager.getInstance().stockOpsService;
	const [group, setGroup] = useState<string>("products");
	const [decision, setDecision] = useState<string>("pending");
	const [sales, setSales] = useState<string>("all");
	const [query, setQuery] = useState<string>("");
	const debounced = useDebounced(query);
	const [rows, setRows] = useState<any[]>([]);
	const [counts, setCounts] = useState<any>(null);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [page, setPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [picked, setPicked] = useState<number[]>([]);
	const [busyId, setBusyId] = useState<number | null>(null);
	const [converting, setConverting] = useState<any | null>(null);
	const [matching, setMatching] = useState<any | null>(null);
	const [exporting, setExporting] = useState<boolean>(false);
	/** Lo que el back pide hacer después (p. ej. un kit pasado al nuevo todavía sin piezas). */
	const [info, setInfo] = useState<string | null>(null);
	const filters = { group, decision, sales, q: debounced.trim() || undefined };

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await http.legacy(campusId, filters, page, 250);
			setRows(response.data.data || []);
			setTotal(response.data.total || 0);
			setLastPage(response.data.last_page || 1);
			setPicked([]);
			http.legacyCounts(campusId).then((r) => setCounts(r.data)).catch(() => undefined);
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo cargar el catálogo viejo."));
		} finally {
			setLoading(false);
		}
	}, [campusId, group, decision, sales, debounced, page]);
	useEffect(() => { load(); }, [load]);

	const act = async (id: number, call: () => Promise<any>) => {
		setBusyId(id);
		setError(null);
		try {
			await call();
			await load();
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudo completar."));
		} finally {
			setBusyId(null);
		}
	};

	const select = (label: string, value: string, options: [string, string][], onChange: (value: string) => void, width = "170px") => <div style={ui.field(width)}>
		<span style={ui.caption}>{label}</span>
		<select className="form-select" style={ui.input} value={value} disabled={idleLocked} onChange={(event) => { onChange(event.target.value); setPage(1); }}>
			{options.map(([key, text]) => <option key={key} value={key}>{text}</option>)}
		</select>
	</div>;

	return <>
		{counts ? <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
			<div style={ui.kpi}><div style={ui.caption}>Productos por revisar</div><div style={{ fontSize: "20px", fontWeight: 700 }}>{counts.pending}</div></div>
			<div style={ui.kpi}><div style={ui.caption}>Con ventas en el último año</div><div style={{ fontSize: "20px", fontWeight: 700, color: "var(--wa-acento-texto)" }}>{counts.pendingWithSales}</div></div>
			<div style={ui.kpi}><div style={ui.caption}>Ya revisados</div><div style={{ fontSize: "20px", fontWeight: 700, color: "var(--wa-verde)" }}>{counts.decided}</div></div>
		</div> : null}
		<div style={{ ...ui.card, ...ui.filters }}>
			<div style={ui.field("240px")}><span style={ui.caption}>Buscar</span>
				<input type="text" className="form-control" style={ui.input} placeholder="Nombre o SKU" value={query} disabled={idleLocked} onChange={(event) => { setQuery(event.target.value); setPage(1); }} /></div>
			{select("Qué", group, [["products", "Productos"], ["services", "Servicios y cobros"], ["all", "Todo"]], setGroup, "160px")}
			{select("Revisión", decision, [["pending", "Por revisar"], ["converted", "Pasados al nuevo"], ["equivalent", "Con equivalente"], ["deprecated", "Dados de baja"], ["keep", "Se quedan por ahora"], ["all", "Todos"]], setDecision)}
			{select("Ventas (12 meses)", sales, [["all", "Todos"], ["with", "Con ventas"], ["without", "Sin ventas"]], setSales, "150px")}
			<button type="button" className="btn btn-outline-success btn-sm rounded-pill" style={{ height: "34px" }} disabled={idleLocked || exporting}
				onClick={async () => {
					setExporting(true);
					setError(null);
					try { saveBlob((await http.legacyExport(campusId, filters)).data, "catalogo_viejo.xlsx", "Inventario"); }
					catch (requestError) { setError(await blobErrorMessage(requestError, "No se pudo generar el Excel.")); }
					finally { setExporting(false); }
				}}>{exporting ? "Generando…" : "Exportar Excel"}</button>
			{canEdit && picked.length > 0 ? <button type="button" className="btn btn-outline-secondary waOroTexto btn-sm rounded-pill" style={{ height: "34px" }} disabled={busyId !== null}
				onClick={() => {
					// Se deshace solo de uno en uno: confirmar antes de dar de baja la página entera.
					if (!window.confirm("Vas a dar de baja " + picked.length + " producto" + (picked.length === 1 ? "" : "s") + " del catálogo viejo. Para deshacerlo hay que hacerlo uno por uno. ¿Continuar?")) return;
					act(-1, () => http.legacyDeprecate(campusId, picked));
				}}>Dar de Baja {picked.length}</button> : null}
		</div>
		{error ? <div className="alert alert-danger" style={{ marginTop: "8px", padding: "8px 12px", fontSize: "14px" }}>{error}</div> : null}
		{info ? <div style={{ marginTop: "8px", padding: "8px 12px", fontSize: "14px", borderRadius: "8px", color: "var(--wa-oro)", background: "var(--wa-oro-suave)", fontWeight: 600, display: "flex", gap: "10px", alignItems: "flex-start" }}>
			<span style={{ flex: 1 }}>{info}</span>
			<button type="button" className="btn-close" aria-label="Cerrar" onClick={() => setInfo(null)} />
		</div> : null}
		<Pager page={page} lastPage={lastPage} total={total} unit="productos" busy={loading} onPage={setPage} />
		<div style={{ ...ui.card, overflowX: "auto" }}>
			{loading ? <Loading /> : rows.length < 1 ? <Empty text="Nada con estos filtros." /> : <table style={{ width: "100%", borderCollapse: "collapse" }}>
				<thead><tr>
					{canEdit ? <th style={{ ...ui.th, width: "30px" }}><input type="checkbox" className="form-check-input" checked={picked.length > 0 && picked.length === rows.filter((row) => !row.decision).length}
						onChange={(event) => setPicked(event.target.checked ? rows.filter((row) => !row.decision).map((row) => row.id) : [])} /></th> : null}
					<th style={ui.th}>Producto</th>
					<th style={ui.th}>Tipo viejo</th>
					<th style={{ ...ui.th, ...ui.right }}>Precio</th>
					<th style={{ ...ui.th, ...ui.right }}>Ventas 12 meses</th>
					<th style={ui.th}>Última venta</th>
					<th style={ui.th}>Revisión</th>
					<th style={ui.th}></th>
				</tr></thead>
				<tbody>{rows.map((row) => <tr key={row.id} style={{ opacity: row.active || row.decision === "converted" ? 1 : 0.6 }}>
					{canEdit ? <td style={ui.td}>{!row.decision ? <input type="checkbox" className="form-check-input" checked={picked.includes(row.id)}
						onChange={(event) => setPicked(event.target.checked ? [...picked, row.id] : picked.filter((id) => id !== row.id))} /> : null}</td> : null}
					<td style={ui.td}><div style={{ fontWeight: 600 }}>{row.name}</div><div style={ui.tiny}>{row.sku}{row.waCode ? " · " + row.waCode : ""}</div></td>
					<td style={ui.td}>{row.type || "-"}<div style={ui.tiny}>{row.category}</div></td>
					<td style={{ ...ui.td, ...ui.right }}>{currency}{Number(row.price).toFixed(2)}</td>
					<td style={{ ...ui.td, ...ui.right, fontWeight: row.sales12m > 0 ? 700 : 400 }}>{row.sales12m}{row.units12m !== row.sales12m && row.units12m ? <div style={ui.tiny}>{row.units12m} piezas</div> : null}</td>
					<td style={ui.td}>{row.lastSold || "-"}</td>
					<td style={ui.td}>
						<span style={{ fontSize: "12px", fontWeight: 600, color: !row.decision ? "var(--wa-apagado)" : row.decision === "deprecated" ? "var(--wa-oro)" : "var(--wa-verde)" }}>{row.decisionLabel}</span>
						{row.replacedByName ? <div style={ui.tiny}>→ {row.replacedByName}</div> : null}
						{row.decidedBy ? <div style={ui.tiny}>{row.decidedBy} · {row.decidedAt}</div> : null}
					</td>
					<td style={ui.td}>{canEdit ? <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end", flexWrap: "wrap" }}>
						{!row.decision ? <>
							<button type="button" className={ui.btnPrimary} disabled={busyId !== null} onClick={() => setConverting(row)}>Pasar al nuevo</button>
							<button type="button" className={ui.btn} disabled={busyId !== null} onClick={() => setMatching(row)}>Equivale a…</button>
							<button type="button" className={ui.btn} disabled={busyId !== null} onClick={() => act(row.id, () => http.legacyKeep(campusId, row.id))}>Dejar</button>
							<button type="button" className="btn btn-outline-secondary waOroTexto btn-sm rounded-pill" disabled={busyId !== null} onClick={() => act(row.id, () => http.legacyDeprecate(campusId, [row.id]))}>Baja</button>
						</> : row.decision !== "converted" ? <button type="button" className={ui.btn} disabled={busyId !== null} onClick={() => act(row.id, () => http.legacyUndo(campusId, row.id))}>Deshacer</button> : null}
					</div> : null}</td>
				</tr>)}</tbody>
			</table>}
		</div>

		{converting ? <ConvertModal row={converting} onCancel={() => setConverting(null)}
			onConfirm={async (kind, name) => {
				const row = converting;
				setConverting(null);
				setInfo(null);
				await act(row.id, async () => { const response = await http.legacyConvert(campusId, row.id, kind, name); if (response?.data?.notice) setInfo(response.data.notice); });
			}} /> : null}
		{matching ? <Modal width="560px" onClose={() => setMatching(null)}>
			<h4 style={{ marginBottom: "4px" }}>¿A qué producto nuevo equivale?</h4>
			<div style={{ ...ui.small, marginBottom: "10px" }}>{matching.name} · {matching.sku}</div>
			<ProductPicker campusId={campusId} kinds={["article", "kit", "uniform", "service", "digital"]} placeholder="Buscar el producto nuevo…"
				onPick={(product: any) => { const row = matching; setMatching(null); act(row.id, () => http.legacyEquivalent(campusId, row.id, product.id)); }} />
		</Modal> : null}
	</>;
};

const ConvertModal: React.FC<{ row: any; onConfirm: (kind: string, name: string) => void; onCancel: () => void }> = ({ row, onConfirm, onCancel }) => {
	const [kind, setKind] = useState<string>(row.isService ? "service" : "article");
	const [name, setName] = useState<string>(row.name);
	return <Modal width="520px" onClose={onCancel}>
		<h4 style={{ marginBottom: "4px" }}>Pasar al catálogo nuevo</h4>
		<div style={{ ...ui.small, marginBottom: "10px" }}>{row.sku} · {row.sales12m} ventas en 12 meses</div>
		<div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "10px" }}>
			{KINDS.map(([key, label]) => <button key={key} type="button" className={"btn btn-sm rounded-pill " + (kind === key ? "btn-primary" : "btn-outline-secondary")} onClick={() => setKind(key)}>{label}</button>)}
		</div>
		<div style={ui.field("100%")}><span style={ui.caption}>Nombre</span>
			<input type="text" className="form-control" style={ui.input} value={name} maxLength={255} onChange={(event) => setName(event.target.value)} /></div>
		<div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
			<button type="button" className="btn btn-outline-secondary rounded-pill" onClick={onCancel}>Cancelar</button>
			<button type="button" className="btn btn-primary rounded-pill" disabled={name.trim().length < 2} onClick={() => onConfirm(kind, name.trim())}>Pasar al nuevo</button>
		</div>
	</Modal>;
};

export default CatalogoViejo;
